import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridFilterModel,
  GridRowId,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetOrionTransactionsQuery } from "features/trading/accounts";
import { gridModelsToQuery } from "features/api";
import {
  OrionTransaction,
  OrionTransactionParams,
  PDFReportSettingsInfo,
  PerformanceReportTypeEnum,
  ReportType,
} from "helpers/types";
import {
  BucketNumberEnum,
  useGetBucketTransactionsQuery,
} from "features/trading/buckets";
import {
  ALL_BUCKETS,
  BucketAssetSelection,
  BucketAssetSelector,
  BucketScope,
} from "components/buckets/BucketAssetSelector";
import {
  ServerDataGrid,
  currencyFormatter,
  dateFormatter,
  qtyFormatter,
  useGridFilters,
  usePagination,
  useSorting,
} from "components/ui/ServerDataGrid";
import { transactionModalStyles } from "styles/TransactionModal.styles";

const columns: GridColDef<OrionTransaction>[] = [
  {
    field: "transaction_date",
    headerName: "Date",
    minWidth: 140,
    flex: 1,
    renderCell: dateFormatter,
  },
  {
    field: "settlement_date",
    headerName: "Settlement Date",
    minWidth: 140,
    flex: 1,
    renderCell: dateFormatter,
  },
  {
    field: "transaction_type",
    headerName: "Type",
    minWidth: 180,
    flex: 1.2,
  },
  {
    field: "ticker",
    headerName: "Ticker",
    minWidth: 120,
    flex: 0.9,
  },
  {
    field: "transaction_amount",
    headerName: "Amount",
    minWidth: 140,
    flex: 1,
    align: "right",
    headerAlign: "right",
    renderCell: currencyFormatter,
  },
  {
    field: "units_amount",
    headerName: "Units",
    minWidth: 120,
    flex: 0.9,
    align: "right",
    headerAlign: "right",
    renderCell: qtyFormatter,
  },
  {
    field: "nav_price",
    headerName: "Nav Price",
    minWidth: 120,
    flex: 0.9,
    align: "right",
    headerAlign: "right",
    renderCell: currencyFormatter,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 130,
    flex: 0.9,
  },
  {
    field: "unit_balance",
    headerName: "Unit Balance",
    minWidth: 120,
    flex: 0.9,
    align: "right",
    headerAlign: "right",
    renderCell: qtyFormatter,
  },
];

// Shown only under "All", where the rows come from two accounts.
const bucketColumn: GridColDef<OrionTransaction> = {
  field: "bucket",
  headerName: "Bucket",
  minWidth: 130,
  flex: 0.9,
};

interface TransactionModalProps {
  open: boolean;
  onClose: () => void;
  settingsInfo: PDFReportSettingsInfo;
  reportType: ReportType;
  // Given only by a report whose calculation honours exclusions (the stock
  // report): without it the grid is read-only, with no selection checkboxes.
  onDeselectedTransactionsChange?: (
    deselectedTransactions: OrionTransaction[],
  ) => void;
}

const TransactionModal = ({
  open,
  onClose,
  reportType,
  settingsInfo,
  onDeselectedTransactionsChange,
}: TransactionModalProps) => {
  const [start_date, end_date] = settingsInfo.period;
  const strategy_id = settingsInfo.strategy_id ?? undefined;
  const orion_account_id = settingsInfo.orion_account_id ?? undefined;
  const [pagination, setPagination] = usePagination(25, 0);
  const [sorting, setSorting] = useSorting("transaction_date", "desc");
  const [gridFilters, setGridFilters] = useGridFilters();
  const [searchValue, setSearchValue] = useState("");
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    GridRowId[]
  >([]);
  const [selectedTransactionsById, setSelectedTransactionsById] = useState<
    Record<number, OrionTransaction>
  >({});

  const isSelectable = Boolean(onDeselectedTransactionsChange);
  const isStrategy = reportType === PerformanceReportTypeEnum.STRATEGY;
  // Null until the bucket selector has loaded its buckets.
  const [selection, setSelection] = useState<BucketAssetSelection | null>(
    null,
  );
  const assetIds = useMemo(() => selection?.assetIds ?? [], [selection]);
  const [scope, setScope] = useState<BucketScope>(ALL_BUCKETS);
  const isBucket3Scope = scope === BucketNumberEnum.BUCKET_3;
  const bucket3Id = settingsInfo.bucket_3_id ?? 0;
  // "All" merges in the Bucket 3 account's rows - unless the user picked
  // specific Bucket 1/2 assets, which none of those rows belong to.
  const includeBucket3 = Boolean(
    isStrategy && selection && scope === ALL_BUCKETS && bucket3Id &&
      !selection.narrowed,
  );

  const canFetch = Boolean(
    start_date &&
      end_date &&
      orion_account_id &&
      (isStrategy ? assetIds.length || includeBucket3 : strategy_id),
  );

  const params = useMemo(() => {
    const baseParams: OrionTransactionParams = {
      start_date: start_date || "",
      end_date: end_date || "",
      orion_account_id: orion_account_id || 0,
      report_type: reportType,
      ...(isStrategy
        ? { asset_ids: assetIds }
        : { strategy_id: strategy_id || 0 }),
      ...(includeBucket3 ? { bucket_3_id: bucket3Id } : {}),
    };

    const queryParams =
      gridModelsToQuery({
        pagination,
        sorting,
        gridFilters,
        filters: baseParams,
      }) || {};

    // Keep `order_by` for compatibility with endpoints expecting this name.
    if (queryParams.ordering && !queryParams.order_by) {
      queryParams.order_by = queryParams.ordering;
    }

    return {
      ...baseParams,
      ...queryParams,
      search: searchValue || undefined,
    };
  }, [
    start_date,
    end_date,
    strategy_id,
    orion_account_id,
    reportType,
    isStrategy,
    assetIds,
    includeBucket3,
    bucket3Id,
    pagination.page,
    pagination.pageSize,
    sorting,
    gridFilters,
    searchValue,
  ]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setGridFilters(model);

      const nextSearch = (model.quickFilterValues || [])
        .map((value) => String(value).trim())
        .filter(Boolean)
        .join(" ");

      setSearchValue(nextSearch);
      setPagination((prev) => ({ ...prev, page: 0 }));
    },
    [setGridFilters, setPagination],
  );

  const { data, isLoading, isFetching, error } = useGetOrionTransactionsQuery(
    params,
    { skip: !open || !canFetch || isBucket3Scope },
  );

  const {
    data: bucket3Transactions,
    isLoading: isLoadingBucket3Transactions,
    isFetching: isFetchingBucket3Transactions,
  } = useGetBucketTransactionsQuery(
    {
      bucketId: bucket3Id,
      start_date: start_date || "",
      end_date: end_date || "",
    },
    { skip: !open || !isBucket3Scope || !bucket3Id || !start_date || !end_date },
  );

  useEffect(() => {
    if (!data?.results?.length) return;

    setSelectedTransactionsById((prev) => {
      const next = { ...prev };
      data.results.forEach((transaction) => {
        if (selectedTransactionIds.includes(transaction.id)) {
          next[transaction.id] = transaction;
        }
      });
      return next;
    });
  }, [data?.results, selectedTransactionIds]);

  const deselectedTransactions = useMemo(
    () =>
      selectedTransactionIds
        .map((id) => selectedTransactionsById[Number(id)])
        .filter(Boolean),
    [selectedTransactionIds, selectedTransactionsById],
  );

  useEffect(() => {
    onDeselectedTransactionsChange?.(deselectedTransactions);
  }, [onDeselectedTransactionsChange, deselectedTransactions]);

  useEffect(() => {
    setSelectedTransactionIds([]);
    setSelectedTransactionsById({});
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [
    settingsInfo.orion_account_id,
    settingsInfo.strategy_id,
    settingsInfo.period?.[0],
    settingsInfo.period?.[1],
    reportType,
    assetIds,
    includeBucket3,
    setPagination,
  ]);

  // The Bucket column exists only while Bucket 3 rows are merged in.
  useEffect(() => {
    if (!includeBucket3) {
      setSorting((prev) => {
        const next = prev.filter(({ field }) => field !== bucketColumn.field);
        return next.length === prev.length ? prev : next;
      });
    }
  }, [includeBucket3, setSorting]);

  const gridColumns = useMemo(
    () => (includeBucket3 ? [bucketColumn, ...columns] : columns),
    [includeBucket3],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Transactions</DialogTitle>
      <DialogContent>
        {isStrategy && (
          <Box sx={{ mb: 2 }}>
            <BucketAssetSelector
              accountId={settingsInfo.account_id}
              showBucket3={Boolean(bucket3Id)}
              scope={scope}
              onSelectionChange={setSelection}
              onScopeChange={setScope}
            />
          </Box>
        )}
        {isBucket3Scope ? (
          <>
            {!isLoadingBucket3Transactions &&
            !isFetchingBucket3Transactions &&
            !bucket3Transactions?.results?.length ? (
              <Typography sx={transactionModalStyles.emptyText}>
                No deposits or withdrawals found.
              </Typography>
            ) : null}
            <ServerDataGrid
              sx={transactionModalStyles.grid}
              rows={bucket3Transactions?.results || []}
              rowCount={bucket3Transactions?.count || 0}
              loading={isLoadingBucket3Transactions || isFetchingBucket3Transactions}
              columns={columns}
              paginationMode="client"
              sortingMode="client"
              filterMode="client"
            />
          </>
        ) : !canFetch ? (
          <Alert severity="info" sx={transactionModalStyles.infoAlert}>
            {isStrategy
              ? "Select a client account and date range, then pick a bucket with assets in it."
              : "Select a client account, date range, and strategy before loading transactions."}
          </Alert>
        ) : error ? (
          <Alert severity="error" sx={transactionModalStyles.errorAlert}>
            Failed to load transactions.
          </Alert>
        ) : (
          <>
            {!isLoading && !isFetching && !data?.results?.length ? (
              <Typography sx={transactionModalStyles.emptyText}>
                No transactions found.
              </Typography>
            ) : null}
            {isSelectable && (
              <Typography sx={transactionModalStyles.selectionInfo}>
                Excluded transactions: {deselectedTransactions.length}
              </Typography>
            )}
            <ServerDataGrid
              sx={transactionModalStyles.grid}
              rows={data?.results || []}
              rowCount={data?.count || 0}
              loading={isLoading || isFetching}
              columns={gridColumns}
              paginationModel={pagination}
              onPaginationModelChange={setPagination}
              sortModel={sorting}
              onSortModelChange={setSorting}
              filterModel={gridFilters}
              onFilterModelChange={handleFilterModelChange}
              {...(isSelectable
                ? {
                    checkboxSelection: true,
                    keepNonExistentRowsSelected: true,
                    rowSelectionModel: selectedTransactionIds,
                    onRowSelectionModelChange: setSelectedTransactionIds,
                  }
                : { rowSelection: false })}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionModal;
